import { Flex, FlexProps, Heading, Text } from 'theme-ui';
import { forwardRef, useMemo } from 'react';
import {
  endOfDay,
  endOfMonth,
  endOfToday,
  format,
  getDate,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfToday,
  toDate
} from 'date-fns';

import ColorTokens from '../../tokens/colors';
import Grid from '../Grid';
import GridChild from '../GridChild';
import { IconButton } from '../IconButton';
import CalendarDot, { CalendarDotProps } from './CalendarDot';

export interface GetCellColorContext {
  disableFuture: boolean | Date;
  disablePast: boolean | Date;
  fadeOtherMonths: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isFuture: boolean;
  isPast: boolean;
  isInAnotherMonth: boolean;
}

export interface CalendarDotItemProps {
  date: Date;
  color: CalendarDotProps['color'];
}

export interface CalendarProps extends Omit<FlexProps, 'ref'> {
  /** Visual annotations of specific cells indicating specific data at specific dates */
  dots?: CalendarDotItemProps[];
  /**
   * Wether past dates selection and months navigation should be disabled (if true, consider "today", if set as date, consider that date, otherwise all past dates are enabled)
   *
   * @example disablePast={true}
   * @example disablePast={startOfMonth()}
   */
  disablePast?: boolean | Date;
  /**
   * Wether future dates selection and months navigation should be disabled (if true, consider "today", if set as date, consider that date, otherwise all past dates are enabled)
   *
   * @example disableFuture={true}
   * @example disableFuture={addWeeks(new Date(), 2)}
   */
  disableFuture?: boolean | Date;
  /**
   * Wether days of other months should be displayed "faded"
   */
  fadeOtherMonths?: boolean;
  /** Callback used to define a given cell text color */
  getCellColor?: (day: Date, context: GetCellColorContext) => string;
  /**
   * Wether days of other months should be hidden
   */
  hideOtherMonths?: boolean;
  /** Array of dates to display (usually the result of "useDateIntervalFromMonth()") */
  interval: Date[];
  /** Callback invoked when a date is selected in the calendar */
  onDateSelected?: (day: Date) => void;
  /** Callback invoked when clicking on previous/next months buttons */
  onMonthChange(inc: boolean): void;
  selectedDay: Date;
}

const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      selectedDay,
      dots = [],
      interval,
      onDateSelected,
      onMonthChange,
      getCellColor,
      disablePast = false,
      disableFuture = false,
      fadeOtherMonths = false,
      hideOtherMonths = false,
      sx,
      ...props
    }: CalendarProps,
    ref
  ): JSX.Element => {
    const today = startOfToday();

    /**
     * Index #10 is arbitrary, we just need a date in the interval that we are certain belongs to the month currently displayed by the calendar
     */
    const intervalReferenceDate = useMemo(() => interval.length > 10 && interval[10], [interval]);

    const disablePreviousMonthNavigation = useMemo(() => {
      const limit = disablePast instanceof Date ? toDate(disablePast) : new Date();

      return (
        !!disablePast &&
        intervalReferenceDate &&
        (isBefore(startOfMonth(intervalReferenceDate), startOfMonth(limit)) ||
          isEqual(startOfMonth(intervalReferenceDate), startOfMonth(limit)))
      );
    }, [intervalReferenceDate, disablePast]);

    const disableNextMonthNavigation = useMemo(() => {
      const limit = disableFuture instanceof Date ? toDate(disableFuture) : new Date();

      return (
        !!disableFuture &&
        intervalReferenceDate &&
        (isAfter(endOfMonth(intervalReferenceDate), endOfMonth(limit)) ||
          isEqual(endOfMonth(intervalReferenceDate), endOfMonth(limit)))
      );
    }, [intervalReferenceDate, disableFuture]);

    return (
      <Flex
        {...props}
        as="div"
        ref={ref}
        sx={{
          flexDirection: 'column',
          borderRadius: 'rounded',
          border: '1px solid',
          borderColor: 'veloGrey2',
          backgroundColor: 'veloWhite',
          ...sx
        }}
        aria-label="calendar"
      >
        <Flex
          as="header"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'veloGrey2',
            px: 's',
            py: 'xs'
          }}
        >
          <IconButton
            aria-label="previous month"
            disabled={disablePreviousMonthNavigation}
            icon="ArrowLeft"
            iconProps={{ circular: true }}
            onClick={(): void => onMonthChange(false)}
            sx={{
              color: disablePreviousMonthNavigation ? 'veloBlueGrey' : 'text',
              '&:enabled': {
                cursor: 'pointer'
              },
              '&:not(:last-child)': {
                mr: 0
              }
            }}
            type="button"
          />
          <Heading variant="heading2">{format(interval[10], 'MMMM yyyy')}</Heading>
          <IconButton
            aria-label="next month"
            disabled={disableNextMonthNavigation}
            icon="ArrowRight"
            iconProps={{ circular: true }}
            onClick={(): void => onMonthChange(true)}
            sx={{
              color: disableNextMonthNavigation ? 'veloBlueGrey' : 'text',
              '&:enabled': {
                cursor: 'pointer'
              }
            }}
            type="button"
          />
        </Flex>
        <Grid columns={7} rowGap="xs" columnGap={0} minColumnWidth="40px" sx={{ m: 0, p: 0, py: 'xs' }} role="grid">
          {interval?.slice(0, 7).map((dayHeader, index) => (
            <GridChild key={`calendar.${selectedDay.getTime()}.header.${index}`} columnSpan={1} align="center">
              <Text
                variant="labelS"
                sx={{
                  py: 'xs',
                  textAlign: 'center',
                  color: [0, 6].includes(getDay(dayHeader)) ? 'systemWarning' : 'veloGrey4'
                }}
              >
                {format(dayHeader, 'iiiii')}
              </Text>
            </GridChild>
          ))}
          {interval.map((day) => {
            // Based on the day of the shift, not the time
            const isDayPast = isBefore(startOfDay(day), startOfToday());
            const isDayFuture = isAfter(endOfDay(day), endOfToday());
            const isDaySelected = isSameDay(day, selectedDay);
            // index 10 is arbitrary, we just need a date in the interval that we are certain
            // belongs to the month currently displayed by the calendar
            const isDayInAnotherMonth = interval.length >= 10 && !isSameMonth(day, interval[10]);

            const pastLimit = startOfDay(disablePast instanceof Date ? toDate(disablePast) : new Date());
            const futureLimit = endOfDay(disableFuture instanceof Date ? toDate(disableFuture) : new Date());

            const isDayDisabled =
              (!!disablePast && isBefore(startOfDay(day), pastLimit)) ||
              (!!disableFuture && isAfter(endOfDay(day), futureLimit)) ||
              /*isDayInAnotherMonth ||*/
              hideOtherMonths;

            return (
              <GridChild key={`calendar.${today.getTime()}.day.${day.getTime()}`} columnSpan={1} align="center">
                <Flex
                  aria-label="calendar cell"
                  sx={{
                    flexFlow: 'column',
                    position: 'relative',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 40,
                    width: 40,
                    cursor: isDayDisabled ? 'not-allowed' : 'pointer',
                    pointerEvents: isDayInAnotherMonth && hideOtherMonths ? 'none' : 'initial'
                  }}
                  onClick={(): void => {
                    if (!!onDateSelected && !isDayDisabled) {
                      onDateSelected(day);
                    }
                  }}
                  role="button"
                >
                  <Flex
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'circular',
                      backgroundColor: isDaySelected ? 'veloDarkBlue' : 'transparent',
                      height: 24,
                      width: 24,
                      mb: 2
                    }}
                  >
                    <Text
                      aria-label="calendar cell content"
                      sx={{
                        display: isDayInAnotherMonth && hideOtherMonths ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // The calendar text style is not tokenized yet
                        fontSize: 14,
                        fontWeight: 'medium',
                        lineHeight: 1,
                        // cell color
                        color: getCellColor
                          ? getCellColor(day, {
                              disableFuture,
                              disablePast,
                              fadeOtherMonths,
                              isDisabled: isDayDisabled,
                              isFuture: isDayFuture,
                              isPast: isDayPast,
                              isSelected: isDaySelected,
                              isInAnotherMonth: isDayInAnotherMonth
                            })
                          : 'text'
                      }}
                    >
                      {getDate(day)}
                    </Text>
                  </Flex>
                  <Flex
                    as="footer"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      height: 10
                    }}
                  >
                    {!!dots &&
                      dots
                        .filter(({ date }) => isSameDay(day, date))
                        .map(({ color }, index) => (
                          <CalendarDot key={`calendar.dot.${day.getTime()}.${index}`} size={8} color={color} />
                        ))}
                  </Flex>
                </Flex>
              </GridChild>
            );
          })}
        </Grid>
      </Flex>
    );
  }
);

Calendar.displayName = 'Calendar';

Calendar.defaultProps = {
  dots: [],
  disableFuture: false,
  disablePast: false,
  fadeOtherMonths: false,
  hideOtherMonths: false,
  getCellColor: (
    _day: Date,
    { fadeOtherMonths, isDisabled, isSelected, isInAnotherMonth }: GetCellColorContext
  ): keyof typeof ColorTokens => {
    if (isSelected) {
      return 'veloPeach';
    } else if (isDisabled || (isInAnotherMonth && fadeOtherMonths)) {
      return 'veloGrey4';
    }

    return 'veloDarkBlue';
  }
};

export default Calendar;
